import "@testing-library/jest-dom";
import { fireEvent, render, screen } from "@testing-library/react";
import { CanvasTimelineRenderer } from "./CanvasTimelineRenderer";
import { useViewDataQuery } from "../../../hooks/useViewDataQuery";

jest.mock("../../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "ws-1" } }),
}));
jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewDataQuery: jest.fn(),
}));
const query = useViewDataQuery as jest.Mock;
const element = {
  id: "timeline-1",
  type: "timeline",
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 4,
  props: {
    title: "Application history",
    data_source_alias: "events",
    label_field: "event",
    status_field: "status",
    date_field: "occurred_at",
    description_field: "notes",
    order_field: "sequence",
  },
};
const dataSources = [
  {
    id: "events",
    name: "events",
    type: "view" as const,
    view_id: 42,
    config: {},
  },
];

describe("CanvasTimelineRenderer", () => {
  it("renders ordered bound stages with status, date and description", () => {
    query.mockReturnValue({
      data: {
        rows: [
          {
            _rowId: "2",
            sequence: 2,
            event: "Approved",
            status: "complete",
            occurred_at: "2026-09-02",
            notes: "Manager approved",
          },
          {
            _rowId: "1",
            sequence: 1,
            event: "Submitted",
            status: "complete",
            occurred_at: "2026-09-01",
            notes: "Sent to HR",
          },
        ],
      },
      isLoading: false,
      error: null,
    });
    render(
      <CanvasTimelineRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(query).toHaveBeenCalledWith(
      "42",
      expect.objectContaining({ canvasId: "canvas-1" }),
    );
    expect(
      screen.getAllByRole("listitem").map((item) => item.textContent),
    ).toEqual([
      expect.stringContaining("Submitted"),
      expect.stringContaining("Approved"),
    ]);
    expect(screen.getByText("Manager approved")).toBeTruthy();
    expect(query).toHaveBeenCalledWith(
      "42",
      expect.objectContaining({
        sort: [{ field: "sequence", direction: "ASC" }],
      }),
    );
  });

  it("renders loading, empty and error states", () => {
    query.mockReturnValueOnce({ isLoading: true, error: null });
    const { rerender } = render(
      <CanvasTimelineRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByText("Loading timeline…")).toBeTruthy();
    query.mockReturnValueOnce({
      data: { rows: [] },
      isLoading: false,
      error: null,
    });
    rerender(
      <CanvasTimelineRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByText("No timeline events yet.")).toBeTruthy();
    query.mockReturnValueOnce({
      isLoading: false,
      error: new Error("Forbidden"),
    });
    rerender(
      <CanvasTimelineRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getByText("Unable to load timeline.")).toBeTruthy();
  });

  it("renders a static stepper from configured steps without querying data", () => {
    query.mockClear();
    render(
      <CanvasTimelineRenderer
        element={{
          ...element,
          props: {
            title: "Request status",
            steps: ["Submitted", "In review", "Approved"],
            current_step: "In review",
          },
        }}
        dataSources={[]}
      />,
    );
    expect(screen.getByText("In review")).toHaveAttribute(
      "aria-current",
      "step",
    );
    expect(query).not.toHaveBeenCalled();
  });

  it("pages through every row instead of silently stopping at the first page", () => {
    const makeRows = (from: number, count: number) =>
      Array.from({ length: count }, (_, index) => ({
        _rowId: String(from + index),
        sequence: from + index,
        event: `Event ${from + index}`,
      }));
    query.mockImplementation((_viewId: string, params: { page?: number }) => ({
      data:
        (params.page ?? 1) === 1
          ? { rows: makeRows(1, 100), totalCount: 130 }
          : { rows: makeRows(101, 30), totalCount: 130 },
      isLoading: false,
      error: null,
    }));
    render(
      <CanvasTimelineRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(screen.getAllByRole("listitem")).toHaveLength(100);
    expect(screen.getByText("Showing 100 of 130 events")).toBeTruthy();
    fireEvent.click(screen.getByRole("button", { name: "Load more" }));
    expect(query).toHaveBeenCalledWith(
      "42",
      expect.objectContaining({ page: 2, pageSize: 100 }),
    );
    expect(screen.getAllByRole("listitem")).toHaveLength(130);
    expect(screen.queryByText(/Showing/)).toBeNull();
    expect(screen.queryByRole("button", { name: "Load more" })).toBeNull();
    query.mockReset();
  });
});
